import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Platform, View } from 'react-native';
import { CameraView } from 'expo-camera';

import { Button } from '../button/Button';
import Text from '../text/Text';

// The installed iOS build (runtime 2.104.0) was built without expo-camera's barcode companion pod,
// so CameraView never reports barcodes there. Apple's own scanner still works; remove this
// fallback once a store build includes ExpoCameraBarcodeScanning.
const USE_APPLE_SCANNER = Platform.OS === 'ios' && CameraView.isModernBarcodeScannerAvailable;
const LAUNCH_DELAY_MS = 500;
const DISMISS_DELAY_MS = 500;

const AppleBarcodeScanner = ({ onBarcodeScanned, barcodeScannerSettings, style }) => {
	const [isUnavailable, setIsUnavailable] = useState(false);
	const onBarcodeScannedRef = useRef(onBarcodeScanned);
	const barcodeTypesRef = useRef(barcodeScannerSettings?.barcodeTypes);
	const isScannerOpenRef = useRef(false);
	const isLaunchingRef = useRef(false);
	const launchTimeoutRef = useRef(null);
	const resultTimeoutRef = useRef(null);

	useEffect(() => {
		onBarcodeScannedRef.current = onBarcodeScanned;
		barcodeTypesRef.current = barcodeScannerSettings?.barcodeTypes;
	}, [barcodeScannerSettings?.barcodeTypes, onBarcodeScanned]);

	const launchScanner = useCallback(async () => {
		clearTimeout(launchTimeoutRef.current);
		// A swiped-away scanner can be reopened, but never twice at once or while a scan is still being handed over.
		if (isLaunchingRef.current || resultTimeoutRef.current) {
			return;
		}
		isLaunchingRef.current = true;
		setIsUnavailable(false);
		isScannerOpenRef.current = true;
		try {
			await CameraView.launchScanner({
				barcodeTypes: barcodeTypesRef.current || [],
				isHighlightingEnabled: true,
			});
		}
		catch (error) {
			isScannerOpenRef.current = false;
			setIsUnavailable(true);
		}
		finally {
			isLaunchingRef.current = false;
		}
	}, []);

	useEffect(() => {
		const subscription = CameraView.onModernBarcodeScanned((result) => {
			// Apple's scanner repeats every code it sees, so only the first one while open counts.
			if (!isScannerOpenRef.current || resultTimeoutRef.current || !onBarcodeScannedRef.current) {
				return;
			}
			isScannerOpenRef.current = false;
			CameraView.dismissScanner();
			// Screens close or open dialogs on a scan, which iOS ignores while the scanner is still animating away.
			resultTimeoutRef.current = setTimeout(() => {
				resultTimeoutRef.current = null;
				onBarcodeScannedRef.current?.(result);
			}, DISMISS_DELAY_MS);
		});
		// Let the surrounding dialog finish presenting before the scanner is presented over it.
		launchTimeoutRef.current = setTimeout(launchScanner, LAUNCH_DELAY_MS);

		return () => {
			clearTimeout(launchTimeoutRef.current);
			clearTimeout(resultTimeoutRef.current);
			resultTimeoutRef.current = null;
			subscription.remove();
			if (isScannerOpenRef.current) {
				isScannerOpenRef.current = false;
				CameraView.dismissScanner();
			}
		};
	}, [launchScanner]);

	return (
		<View style={[style, styles.container]}>
			<Text style={styles.text}>
				{isUnavailable
					? 'The barcode scanner isn\'t available on this phone.'
					: 'Swipe the scanner down to close it.'}
			</Text>
			<Button
				label="Scan barcode"
				onPress={launchScanner}
			/>
		</View>
	);
};

const BarcodeCameraView = (props) => {
	if (USE_APPLE_SCANNER) {
		return <AppleBarcodeScanner {...props} />;
	}

	return <CameraView {...props} />;
};

const styles = {
	container: {
		alignItems: 'center',
		justifyContent: 'center',
		padding: 16,
		backgroundColor: '#000',
	},
	text: {
		color: '#fff',
		textAlign: 'center',
		marginBottom: 12,
	},
};

export default BarcodeCameraView;
